import { useEffect } from 'react';
import { api } from '../utils/api';
import { useEditorStore, DEFAULT_DC_SETTINGS } from '../store/editorStore';
import { mapApiRack } from '../utils/draftUtils';
import { PPM } from '../store/editorStore';
import { aisleZoneToEditor, trayNodeToEditor } from '../utils/floorPlan.logic';
import { editorLoadPaths, normalizeDraftRackDiff, preserveUnsavedEditorRacks } from './useDraftLoader.logic';
import { normalizeRackStatus, normalizeRackRole } from '../utils/rackLifecycle.logic';
import { placementFromSource } from '../utils/placement.logic';

export function useDraftLoader(code: string | undefined, currentDraftId: number | null, dc: any) {
  useEffect(() => {
    if (!code || !currentDraftId) return;
    const paths = editorLoadPaths(code, currentDraftId)
    if (!paths.draft) return
    let active = true
    const controller = new AbortController()
    const options = { signal: controller.signal }

    Promise.all([
      api.get(paths.draft, options),
      api.get(paths.racks, options),
      api.get(`/api/datacenters/${code}/hierarchy`, options).catch(() => ({ data: [] })),
    ]).then(([draftRes, racksRes, hierarchyRes]) => {
      if (!active) return
      const draft = draftRes.data;
      const masterRacks = racksRes.data || [];
      const moduleCodes = Object.fromEntries(
        (hierarchyRes.data || []).flatMap((hall: any) => hall.modules || [])
          .filter((module: any) => module?.id && module?.code)
          .map((module: any) => [module.id, module.code]),
      );
      const diff = normalizeDraftRackDiff(draft.racks_json);
      const added = Array.isArray(draft.racks_json)
        ? draft.racks_json
        : (diff?.added || []);
      if (preserveUnsavedEditorRacks(useEditorStore.getState().racks, added, masterRacks.length)) {
        console.warn('[Editor] skipped stale draft load — unsaved racks present');
        return;
      }

      let racksRecord: Record<string, any> = {};
      
      masterRacks.forEach((rack: any) => {
        const mapped = mapApiRack(rack);
        racksRecord[mapped.id] = mapped;
      });
      
      useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));

      if (Array.isArray(draft.racks_json)) {
        racksRecord = {};
        draft.racks_json.forEach((rack: any) => {
          const source = masterRacks.find((master: any) => String(master.id) === String(rack.db_id ?? rack.dbId ?? rack.id));
          const mapped = mapApiRack({
            ...(source ? { u_slots: source.u_slots, max_weight_kg: source.max_weight_kg, max_power_w: source.max_power_w } : {}),
            ...rack,
          }, true);
          racksRecord[mapped.id] = mapped;
        });
      } else if (diff) {
        const deletedIds = diff.deleted || [];
        deletedIds.forEach((dbId: number | string) => {
          const key = Object.keys(racksRecord).find(k => String(racksRecord[k].dbId) === String(dbId));
          if (key) delete racksRecord[key];
        });
        
        const updated = diff.updated || {};
        Object.entries(updated).forEach(([dbIdStr, rack]: [string, any]) => {
          const dbId = parseInt(dbIdStr, 10);
          const key = Object.keys(racksRecord).find(k => String(racksRecord[k].dbId) === String(dbId));
          if (key) {
             const existing = racksRecord[key];
             if (rack.x_m != null) existing.x = Math.round(rack.x_m * PPM);
             if (rack.y_m != null) existing.y = Math.round(rack.y_m * PPM);
             if (rack.row_index != null) existing.rowIndex = rack.row_index;
             if (rack.rack_index != null) existing.rackIndex = rack.rack_index;
             if (rack.rotation != null) existing.rotation = rack.rotation;
             if (rack.type) existing.rackType = rack.type;
             if (rack.u_slots !== undefined) existing.uCount = rack.u_slots;
             if (rack.is_facility != null) existing.isFacility = Boolean(rack.is_facility);
             if (rack.is_blocked != null) existing.isBlocked = Boolean(rack.is_blocked);
             if (rack.tenant !== undefined) existing.tenant = rack.tenant;
             if (rack.status) existing.status = normalizeRackStatus(rack.status);
             if (rack.role) existing.role = normalizeRackRole(rack.role, existing.rackType);
             if (rack.row_id !== undefined) existing.rowId = rack.row_id;
             if (rack.module_id !== undefined) existing.moduleId = rack.module_id;
             if (rack.rack_pos_in_row !== undefined) existing.rackPosInRow = rack.rack_pos_in_row;
             if (rack.label !== undefined) existing.label = rack.label;
             if (rack.max_power_w !== undefined) existing.max_power_w = rack.max_power_w;
             if (rack.max_weight_kg !== undefined) existing.max_weight_kg = rack.max_weight_kg;
             // Проблема 5: эти поля раньше не применялись → физические габариты сбрасывались
             if (rack.width_m !== undefined) { existing.widthM = rack.width_m; existing.width = Math.round((rack.width_m ?? dc?.rack_step_m ?? .6) * PPM); }
             if (rack.depth_m !== undefined) { existing.depthM = rack.depth_m; existing.height = Math.round((rack.depth_m ?? dc?.rack_depth_m ?? 1) * PPM); }
             if (rack.height_m !== undefined) existing.heightM = rack.height_m;
             existing.placement = placementFromSource(existing);
          }
        });
        
        (diff.added || []).forEach((rack: any) => {
          const mapped = mapApiRack(rack, true);
          racksRecord[mapped.id] = mapped;
        });
      }

      const cableTraysData = draft.cable_trays_json || {};
      const draftNamingProfiles = cableTraysData.dc_settings || {};
      const namingProfilesData = draftNamingProfiles.namingProfiles
        ? { profiles: draftNamingProfiles.namingProfiles, assignments: draftNamingProfiles.namingAssignments }
        : (dc?.naming_profiles_json || {});
      if (cableTraysData.racks_meta) {
        Object.entries(cableTraysData.racks_meta).forEach(([id, meta]: [string, any]) => {
          if (racksRecord[id]) {
            if (meta.status) racksRecord[id].status = normalizeRackStatus(meta.status);
            if (meta.role) racksRecord[id].role = normalizeRackRole(meta.role, racksRecord[id].rackType);
          }
        });
      }

      useEditorStore.getState().loadRacks(racksRecord);
      if (cableTraysData.rows_meta && Object.keys(cableTraysData.rows_meta).length > 0) {
        useEditorStore.getState().loadRows(cableTraysData.rows_meta);
      } else {
        api.get(`/api/datacenters/${code}/rows`, options).then((rowsRes) => {
          if (!active) return
          const rowsList: any[] = rowsRes.data || [];
          const rowsRecord: Record<string, any> = {};
          rowsList.forEach((r: any) => {
            rowsRecord[r.id] = {
               ...(r.aggregate_json || {}),
               id: r.id,
               name: r.name,
               moduleId: r.module_id ?? undefined,
               labelIndex: r.label_index,
              orientation: r.orientation ?? 0,
              color: r.color ?? null,
            };
          });
          useEditorStore.getState().loadRows(rowsRecord);
        }).catch(() => {
          if (!active) return
          useEditorStore.getState().loadRows({});
        });
      }
      if (cableTraysData.dc_settings) {
        useEditorStore.getState().loadDcSettings({
          ...DEFAULT_DC_SETTINGS,
          namingMask: dc?.rack_naming_template || DEFAULT_DC_SETTINGS.namingMask,
          module: dc?.prefix || DEFAULT_DC_SETTINGS.module,
          defaultRackWidthM: dc?.rack_step_m || DEFAULT_DC_SETTINGS.defaultRackWidthM,
          defaultRackDepthM: dc?.rack_depth_m || DEFAULT_DC_SETTINGS.defaultRackDepthM,
          defaultRackHeightM: dc?.rack_height_m || DEFAULT_DC_SETTINGS.defaultRackHeightM,
          defaultAisleWidthM: dc?.aisle_width_m || DEFAULT_DC_SETTINGS.defaultAisleWidthM,
          defaultTrayHeightM: dc?.tray_above_rack_m || DEFAULT_DC_SETTINGS.defaultTrayHeightM,
          ...(dc?.floor_plan_json ? { floorPlan: dc.floor_plan_json } : {}),
          ...(dc?.structured_geometry_json ? { structuredGeometry: dc.structured_geometry_json } : {}),
          // Saved draft fields, including explicit empty geometry, override Production.
          ...cableTraysData.dc_settings,
          planning: cableTraysData.dc_settings.planning ?? cableTraysData.planning ?? { placements: [], rack_reservations: [] },
          moduleCodes,
          ...(namingProfilesData.profiles ? { namingProfiles: namingProfilesData.profiles } : {}),
          ...(namingProfilesData.assignments ? { namingAssignments: namingProfilesData.assignments } : {}),
        });
      } else if (namingProfilesData.profiles || dc) {
        useEditorStore.getState().loadDcSettings({
          ...DEFAULT_DC_SETTINGS,
          moduleCodes,
          planning: cableTraysData.planning ?? { placements: [], rack_reservations: [] },
          namingMask: dc?.rack_naming_template || DEFAULT_DC_SETTINGS.namingMask,
          module: dc?.prefix || DEFAULT_DC_SETTINGS.module,
          defaultRackWidthM: dc?.rack_step_m || DEFAULT_DC_SETTINGS.defaultRackWidthM,
          defaultRackDepthM: dc?.rack_depth_m || DEFAULT_DC_SETTINGS.defaultRackDepthM,
          defaultRackHeightM: dc?.rack_height_m || DEFAULT_DC_SETTINGS.defaultRackHeightM,
          defaultAisleWidthM: dc?.aisle_width_m || DEFAULT_DC_SETTINGS.defaultAisleWidthM,
          defaultTrayHeightM: dc?.tray_above_rack_m || DEFAULT_DC_SETTINGS.defaultTrayHeightM,
          namingProfiles: namingProfilesData.profiles,
          namingAssignments: namingProfilesData.assignments,
          ...(dc?.floor_plan_json ? { floorPlan: dc.floor_plan_json } : {}),
          ...(dc?.structured_geometry_json ? { structuredGeometry: dc.structured_geometry_json } : {}),
        });
      }

      // Проблема 8: загружаем aisle_zones из черновика
      {
        const zonesRecord: Record<string, any> = {};
        const zones = Object.prototype.hasOwnProperty.call(cableTraysData, 'aisle_zones') ? cableTraysData.aisle_zones : dc?.aisle_zones_json;
        Object.entries(zones || {}).forEach(([id, z]: [string, any]) => {
          zonesRecord[id] = aisleZoneToEditor(z, PPM);
        });
        useEditorStore.getState().loadAisleZones(zonesRecord);
      }

      if (draft.cable_trays_json) {
        const { nodes = {}, edges = {} } = draft.cable_trays_json;
        const parseNode = (n: any) => trayNodeToEditor(n, PPM);
        const nodesRec = Array.isArray(nodes) ? Object.fromEntries(nodes.map((n: any) => [n.id, parseNode(n)])) : Object.fromEntries(Object.entries(nodes).map(([k, n]: any) => [k, parseNode(n)]));
        const edgesRec = Array.isArray(edges) ? Object.fromEntries(edges.map((e: any) => [e.id, e])) : edges;
        useEditorStore.getState().setTrayGraph(nodesRec, edgesRec);
      } else {
        useEditorStore.getState().setTrayGraph({}, {});
      }
    }).catch(err => {
      if (!active) return
      console.error('[Editor] Failed to load draft:', err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    });
    return () => { active = false; controller.abort() }
  }, [code, currentDraftId, dc?.id]);

  useEffect(() => {
    if (!code || currentDraftId) return;
    const { racks: racksPath } = editorLoadPaths(code, null)
    let active = true
    const controller = new AbortController()
    const options = { signal: controller.signal }
    
    Promise.all([
      api.get(racksPath, options),
      api.get(`/api/datacenters/${code}/hierarchy`, options).catch(() => ({ data: [] })),
    ]).then(([racksRes, hierarchyRes]) => {
      if (!active) return
      const apiRacks: any[] = racksRes.data || [];
      const moduleCodes = Object.fromEntries(
        (hierarchyRes.data || []).flatMap((hall: any) => hall.modules || [])
          .filter((module: any) => module?.id && module?.code)
          .map((module: any) => [module.id, module.code]),
      );
      api.get(`/api/datacenters/${code}/rows`, options).then((rowsRes) => {
        if (!active) return
        const rowsList: any[] = rowsRes.data || [];
        const rowsRecord: Record<string, any> = {};
        rowsList.forEach((r: any) => {
          rowsRecord[r.id] = {
            ...(r.aggregate_json || {}),
            id: r.id,
            name: r.name,
            moduleId: r.module_id ?? undefined,
            labelIndex: r.label_index ?? 1,
            orientation: r.orientation ?? 0,
            color: r.color ?? null,
          };
        });
        useEditorStore.getState().loadRows(rowsRecord);
      }).catch(() => {
        if (!active) return
        useEditorStore.getState().loadRows({});
      });

      const racksRecord: Record<string, any> = {};
      apiRacks.forEach((rack: any) => {
        const mapped = mapApiRack(rack);
        racksRecord[mapped.id] = mapped;
      });
      useEditorStore.getState().loadRacks(racksRecord);
      useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));

      if (dc) {
        useEditorStore.getState().loadDcSettings({
          ...DEFAULT_DC_SETTINGS,
          moduleCodes,
          planning: dc.cable_trays_json?.planning ?? { placements: [], rack_reservations: [] },
          namingMask: dc.rack_naming_template || DEFAULT_DC_SETTINGS.namingMask,
          module: dc.prefix || DEFAULT_DC_SETTINGS.module,
          defaultRackWidthM: dc.rack_step_m || DEFAULT_DC_SETTINGS.defaultRackWidthM,
          defaultRackDepthM: dc.rack_depth_m || DEFAULT_DC_SETTINGS.defaultRackDepthM,
          defaultRackHeightM: dc.rack_height_m || DEFAULT_DC_SETTINGS.defaultRackHeightM,
          defaultAisleWidthM: dc.aisle_width_m || DEFAULT_DC_SETTINGS.defaultAisleWidthM,
          defaultTrayHeightM: dc.tray_above_rack_m || DEFAULT_DC_SETTINGS.defaultTrayHeightM,
          ...(dc.naming_profiles_json?.profiles ? { namingProfiles: dc.naming_profiles_json.profiles } : {}),
          ...(dc.naming_profiles_json?.assignments ? { namingAssignments: dc.naming_profiles_json.assignments } : {}),
          ...(dc.floor_plan_json ? { floorPlan: dc.floor_plan_json } : {}),
          ...(dc.structured_geometry_json ? { structuredGeometry: dc.structured_geometry_json } : {}),
        });
      }

      if (dc?.aisle_zones_json && Object.keys(dc.aisle_zones_json).length > 0) {
        const zonesRecord: Record<string, any> = {};
        Object.entries(dc.aisle_zones_json).forEach(([id, z]: [string, any]) => {
          zonesRecord[id] = aisleZoneToEditor(z, PPM);
        });
        useEditorStore.getState().loadAisleZones(zonesRecord);
      }

      if (dc?.cable_trays_json) {
        const { nodes = {}, edges = {} } = dc.cable_trays_json;
        const parseNode = (n: any) => trayNodeToEditor(n, PPM);
        const nodesRec = Array.isArray(nodes) 
          ? Object.fromEntries(nodes.map((n: any) => [n.id, parseNode(n)])) 
          : Object.fromEntries(Object.entries(nodes).map(([k, n]: any) => [k, parseNode(n)]));
        const edgesRec = Array.isArray(edges) ? Object.fromEntries(edges.map((e: any) => [e.id, e])) : edges;
        useEditorStore.getState().setTrayGraph(nodesRec, edgesRec);
      } else {
        useEditorStore.getState().setTrayGraph({}, {});
      }
    }).catch(err => {
      if (!active) return
      console.error('[Editor] Failed to load published data:', err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    });
    return () => { active = false; controller.abort() }
  }, [code, currentDraftId, dc]);
}
